import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { ShieldCheckIcon } from '@heroicons/react/24/solid';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useAuth } from './AuthContext';

export default function Login() {
  const { login } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();

  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');

  // After login, go back to where the user was trying to go, or the dashboard
  const from = location.state?.from?.pathname || '/ciso-dashboard';

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email.trim().toLowerCase(), password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.message || 'Invalid email or password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 py-12">
      <div className="max-w-md w-full px-6">

        {/* Back to home */}
        <Link
          to="/"
          className="inline-flex items-center space-x-2 text-sm text-gray-400 hover:text-white mb-6 transition-colors"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          <span>Back to home</span>
        </Link>

        <div className="bg-gray-800 rounded-lg shadow-xl p-8 border border-gray-700">

          {/* Logo */}
          <div className="flex justify-center mb-8">
            <div className="bg-primary-600 p-4 rounded-full">
              <ShieldCheckIcon className="h-12 w-12 text-white" />
            </div>
          </div>

          {/* Title */}
          <h1 className="text-3xl font-bold text-center mb-2 bg-gradient-to-r from-primary-400 to-primary-600 bg-clip-text text-transparent">
            CISO Advisor
          </h1>
          <p className="text-gray-400 text-center mb-8">
            AI-Powered Virtual CISO
          </p>

          {/* Login Form */}
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-white placeholder-gray-400"
                placeholder="you@example.com"
                required
                autoFocus
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-white placeholder-gray-400"
                placeholder="••••••••"
                required
              />
            </div>

            {error && (
              <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary-600 hover:bg-primary-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          {/* Divider */}
          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-700"></div>
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-gray-800 px-3 text-gray-500">New here?</span>
            </div>
          </div>

          {/* Sign-up paths */}
          <div className="space-y-3">
            <Link
              to="/lead-capture"
              className="block w-full text-center bg-gray-700 hover:bg-gray-600 border border-gray-600 text-white font-semibold py-3 px-4 rounded-lg transition-colors"
            >
              Request a Demo
            </Link>
            <Link
              to="/tier-selection"
              className="block w-full text-center bg-gray-700 hover:bg-gray-600 border border-gray-600 text-white font-semibold py-3 px-4 rounded-lg transition-colors"
            >
              Browse Pricing Tiers
            </Link>
            <Link
              to="/"
              className="block w-full text-center text-primary-400 hover:text-primary-300 text-sm font-semibold py-2 transition-colors"
            >
              Learn more about CISO Advisor →
            </Link>
          </div>

        </div>

        {/* Footer */}
        <p className="text-center text-gray-500 text-sm mt-8">
          Quantitative Risk Assessment • Expected Annual Loss Calculations
        </p>
      </div>
    </div>
  );
}
